import React, { useMemo, useState } from "react";
import axios from "axios";
import "./AccountAccess.css";
import { buildApiUrl } from "../../lib/api";

function getErrorMessage(error, fallback) {
    const payload = error?.response?.data;
    if (typeof payload?.message === "string" && payload.message.trim()) {
        return payload.message;
    }
    return fallback;
}

function AccountAccess({ mode, onModeChange, onAuthSuccess, onClose }) {
    const isRegister = mode === "register";
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const header = useMemo(() => {
        return isRegister ? "get started" : "welcome back";
    }, [isRegister]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setErrorMessage("");
        setIsSubmitting(true);

        try {
            const endpoint = isRegister ? "/auth/register" : "/auth/login";
            const { data } = await axios.post(
                buildApiUrl(endpoint),
                {
                    email,
                    password,
                },
                { withCredentials: true }
            );

            if (data?.status === "success" && data?.user) {
                onAuthSuccess(data.user, {
                    openOnboarding: isRegister,
                });
                return;
            }

            setErrorMessage(data?.message || "We could not complete that request.");
        } catch (error) {
            setErrorMessage(
                getErrorMessage(
                    error,
                    isRegister
                        ? "Account creation failed. Please try again."
                        : "Login failed. Please try again."
                )
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="account-access-shell">
            <div className="account-access-card">
                <div className="account-access-header-row">
                    <h1>{header}</h1>
                    <button
                        type="button"
                        className="account-access-close"
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        ×
                    </button>
                </div>
                <form className="account-access-form" onSubmit={handleSubmit}>
                    <div className="account-access-mode-switch">
                        <button
                            type="button"
                            className={`account-access-mode-pill ${isRegister ? "active" : ""}`}
                            onClick={() => onModeChange("register")}
                        >
                            Open Account
                        </button>
                        <button
                            type="button"
                            className={`account-access-mode-pill ${!isRegister ? "active" : ""}`}
                            onClick={() => onModeChange("login")}
                        >
                            Log In
                        </button>
                    </div>

                    <label className="account-access-field">
                        <span className="account-access-label">Email</span>
                        <input
                            type="email"
                            placeholder="Email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            autoComplete="email"
                            required
                        />
                    </label>

                    <label className="account-access-field account-access-field-password">
                        <span className="account-access-label">Password</span>
                        <input
                            type={showPassword ? "text" : "password"}
                            placeholder={isRegister ? "At least 8 characters" : "Password"}
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            autoComplete={isRegister ? "new-password" : "current-password"}
                            required
                        />
                        <button
                            type="button"
                            className="account-access-toggle"
                            onClick={() => setShowPassword((prev) => !prev)}
                        >
                            {showPassword ? "Hide" : "Show"}
                        </button>
                    </label>

                    {errorMessage ? (
                        <p className="account-access-error">{errorMessage}</p>
                    ) : null}

                    <div className="account-access-actions">
                        <button
                            type="submit"
                            className="account-access-button account-access-button-primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting
                                ? (isRegister ? "Creating..." : "Logging in...")
                                : (isRegister ? "Create account" : "Log In")}
                        </button>

                        <button
                            type="button"
                            className="account-access-button account-access-button-secondary"
                            disabled={isSubmitting}
                            onClick={() => onModeChange(isRegister ? "login" : "register")}
                        >
                            {isRegister ? "I already have an account" : "Open a new account"}
                        </button>
                    </div>
                </form>

                <div className="account-access-footer">
                    Local development auth is enabled in this environment, so this flow stays
                    usable even without the hosted identity provider.
                </div>
            </div>
        </section>
    );
}

export default AccountAccess;
